/**
 * CombatGoldDisplay - Displays permanent gold rewards generated by kills during combat.
 *
 * Responsibilities:
 * - Listen for CombatKillGoldEvent messages from the backend
 * - Track gold gains per player during the current combat
 * - Provide render data showing individual gold gains and totals
 * - Support visual display of "+N gold" for each kill event
 * - Reset gold tracking when combat ends or phase changes
 * - Distinguish between current player's gold gains and opponent's
 *
 * The backend is authoritative; this component only renders gold information
 * that the server sends via CombatKillGoldEvent messages.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import {
  CombatKillGoldEvent,
  CombatResult,
  ServerToClientMessage,
} from '../../shared/messages/MessageContracts';

// ─── Gold Gain Entry ────────────────────────────────────────────────────────

/**
 * A single gold gain event from a kill.
 */
export interface CombatGoldGainEntry {
  /** Unique identifier for this gold gain (derived from killer + victim). */
  id: string;

  /** Instance ID of the minion that scored the kill. */
  killerInstanceId: string;

  /** Player ID of the killer. */
  killerPlayerId: string;

  /** Instance ID of the killed minion (victim). */
  victimInstanceId: string;

  /** Amount of gold awarded. */
  goldAwarded: number;

  /** Whether this gold gain belongs to the current player. */
  isCurrentPlayer: boolean;

  /** Whether this entry has been consumed (animated/displayed). */
  consumed: boolean;

  /** Sequence index from the event stream. */
  sequenceIndex: number;
}

// ─── Combat Gold Render Data ────────────────────────────────────────────────

/**
 * Render data for the combat gold display.
 */
export interface CombatGoldDisplayRenderData {
  /** Whether the gold display is active (combat in progress). */
  isActive: boolean;

  /** All gold gain entries for the current combat. */
  goldGains: CombatGoldGainEntry[];

  /** Pending (unconsumed) gold gain entries. */
  pendingGains: CombatGoldGainEntry[];

  /** Consumed gold gain entries. */
  consumedGains: CombatGoldGainEntry[];

  /** Total gold gained by the current player in this combat. */
  currentPlayerTotalGold: number;

  /** Total gold gained by the opponent in this combat. */
  opponentTotalGold: number;

  /** Number of pending gold gains. */
  pendingCount: number;

  /** Number of consumed gold gains. */
  consumedCount: number;

  /** Total number of gold gains. */
  totalCount: number;

  /** Whether all gold gains have been consumed. */
  allConsumed: boolean;

  /** Whether combat has ended. */
  combatEnded: boolean;

  /** Next pending gold gain entry (if any). */
  nextGain: CombatGoldGainEntry | null;
}

/**
 * Callback invoked when render data changes.
 */
export type CombatGoldDisplayRenderCallback = (data: CombatGoldDisplayRenderData) => void;

/**
 * Callback invoked when a new gold gain is recorded.
 */
export type NewGoldGainCallback = (entry: CombatGoldGainEntry) => void;

// ─── Combat Gold Display ────────────────────────────────────────────────────

export class CombatGoldDisplay {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: CombatGoldDisplayRenderCallback[] = [];
  private _newGoldCallbacks: NewGoldGainCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal state
  private _goldGains: CombatGoldGainEntry[] = [];
  private _combatActive: boolean = false;
  private _combatEnded: boolean = false;
  private _lastProcessedGoldKey: string = '';
  private _lastProcessedResultKey: string = '';
  private _sequenceCounter: number = 0;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): CombatGoldDisplayRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the gold display is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns true if combat is active and gold gains are being tracked. */
  get isActive(): boolean {
    return this._combatActive;
  }

  /** Returns true if combat has ended. */
  get combatHasEnded(): boolean {
    return this._combatEnded;
  }

  /** Returns the total gold gained by the current player. */
  get currentPlayerTotalGold(): number {
    return this._goldGains
      .filter((g) => g.isCurrentPlayer)
      .reduce((sum, g) => sum + g.goldAwarded, 0);
  }

  /** Returns the total gold gained by the opponent. */
  get opponentTotalGold(): number {
    return this._goldGains
      .filter((g) => !g.isCurrentPlayer)
      .reduce((sum, g) => sum + g.goldAwarded, 0);
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the gold display and start listening for combat events.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the gold display and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset all gold tracking state (called when combat ends or phase changes).
   */
  reset(): void {
    this._goldGains = [];
    this._combatActive = false;
    this._combatEnded = false;
    this._lastProcessedGoldKey = '';
    this._lastProcessedResultKey = '';
    this._sequenceCounter = 0;
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   */
  onRenderChange(callback: CombatGoldDisplayRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to new gold gain notifications.
   */
  onNewGoldGain(callback: NewGoldGainCallback): () => void {
    this._newGoldCallbacks.push(callback);
    return () => {
      const index = this._newGoldCallbacks.indexOf(callback);
      if (index !== -1) {
        this._newGoldCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Consumption API ─────────────────────────────────────────────────────

  /**
   * Consume the next pending gold gain entry in order.
   * Returns the entry if available, null if no pending entries.
   */
  consumeNextGain(): CombatGoldGainEntry | null {
    for (let i = 0; i < this._goldGains.length; i++) {
      if (!this._goldGains[i].consumed) {
        this._goldGains[i].consumed = true;
        this._notifyRenderCallbacks();
        return this._goldGains[i];
      }
    }
    return null;
  }

  /**
   * Peek at the next pending gold gain without consuming it.
   */
  peekNextGain(): CombatGoldGainEntry | null {
    for (let i = 0; i < this._goldGains.length; i++) {
      if (!this._goldGains[i].consumed) {
        return this._goldGains[i];
      }
    }
    return null;
  }

  /**
   * Consume all pending gold gains at once.
   */
  consumeAllGains(): CombatGoldGainEntry[] {
    const consumed: CombatGoldGainEntry[] = [];
    let entry;
    while ((entry = this.consumeNextGain()) !== null) {
      consumed.push(entry);
    }
    return consumed;
  }

  // ─── Manual Gold Gain Recording ──────────────────────────────────────────

  /**
   * Manually record a gold gain (for testing or direct API use).
   * In production, gold gains are automatically captured from backend events.
   */
  recordGoldGain(
    killerInstanceId: string,
    killerPlayerId: string,
    victimInstanceId: string,
    goldAwarded: number,
  ): void {
    const entry: CombatGoldGainEntry = {
      id: `gold-${killerInstanceId}-${victimInstanceId}-${this._sequenceCounter++}`,
      killerInstanceId,
      killerPlayerId,
      victimInstanceId,
      goldAwarded,
      isCurrentPlayer: killerPlayerId === this._playerId,
      consumed: false,
      sequenceIndex: this._sequenceCounter - 1,
    };

    this._goldGains.push(entry);

    for (const callback of this._newGoldCallbacks) {
      callback(entry);
    }

    this._notifyRenderCallbacks();
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const state = event.data as { match: any };
        const matchState = state.match;

        // Detect combat start
        if (matchState.combatStarted && !this._combatActive) {
          this._combatActive = true;
          this._combatEnded = false;
        }

        // Process combat kill gold events
        if (matchState.lastCombatKillGold && this._combatActive) {
          const goldKey = `${matchState.lastCombatKillGold.killerInstanceId}-${matchState.lastCombatKillGold.victimInstanceId}-${matchState.lastCombatKillGold.goldAwarded}`;
          if (goldKey !== this._lastProcessedGoldKey) {
            this._lastProcessedGoldKey = goldKey;
            this._handleKillGold(matchState.lastCombatKillGold);
          }
        }

        // Process combat result (marks combat ended)
        if (matchState.lastCombatResult && this._combatActive) {
          const resultKey = `${matchState.lastCombatResult.matchId}-${matchState.lastCombatResult.winnerId ?? 'null'}`;
          if (resultKey !== this._lastProcessedResultKey) {
            this._lastProcessedResultKey = resultKey;
            this._combatEnded = true;
          }
        }

        // Reset when phase returns to preparation or match ends
        if (matchState.phase === 'preparation' || matchState.gameOver) {
          this.reset();
        }

        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _handleKillGold(data: CombatKillGoldEvent): void {
    const entry: CombatGoldGainEntry = {
      id: `gold-${data.killerInstanceId}-${data.victimInstanceId}-${this._sequenceCounter++}`,
      killerInstanceId: data.killerInstanceId,
      killerPlayerId: data.killerPlayerId,
      victimInstanceId: data.victimInstanceId,
      goldAwarded: data.goldAwarded,
      isCurrentPlayer: data.killerPlayerId === this._playerId,
      consumed: false,
      sequenceIndex: this._sequenceCounter - 1,
    };

    this._goldGains.push(entry);

    for (const callback of this._newGoldCallbacks) {
      callback(entry);
    }
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _buildRenderData(): CombatGoldDisplayRenderData {
    const pendingGains = this._goldGains.filter((g) => !g.consumed);
    const consumedGains = this._goldGains.filter((g) => g.consumed);
    const currentPlayerTotal = this._goldGains
      .filter((g) => g.isCurrentPlayer)
      .reduce((sum, g) => sum + g.goldAwarded, 0);
    const opponentTotal = this._goldGains
      .filter((g) => !g.isCurrentPlayer)
      .reduce((sum, g) => sum + g.goldAwarded, 0);
    const nextGain = pendingGains.length > 0 ? pendingGains[0] : null;

    return {
      isActive: this._combatActive,
      goldGains: [...this._goldGains],
      pendingGains: [...pendingGains],
      consumedGains: [...consumedGains],
      currentPlayerTotalGold: currentPlayerTotal,
      opponentTotalGold: opponentTotal,
      pendingCount: pendingGains.length,
      consumedCount: consumedGains.length,
      totalCount: this._goldGains.length,
      allConsumed: pendingGains.length === 0,
      combatEnded: this._combatEnded,
      nextGain,
    };
  }
}
